import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";

import { notBlank } from "../../util/ext";
import { useHistory, useLocation, useParams } from "react-router-dom";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { Box } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import Header from "../../creator-application/common/Header";
import {
  generateFilename,
  getDownloadUrl,
  useContentProvider,
  useContentProviderEndpoint,
  useGetJobChunks,
  useGetJobDetail,
} from "./processingDiscoveryServices";
import JobChunksTable from "./JobChunksTable";
import Grid from "@material-ui/core/Grid";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import ReadOnlyField from "../../components/ReadOnlyField";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import DownloadIcon from "@material-ui/icons/SaveAlt";

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    root: {
      border: "3px white solid",
      backgroundColor: "white",
      borderRadius: "20px",
    },

    panel: {
      backgroundColor: "white",
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },
  }));

const ContentIngestionJobDetailView = () => {
  const [scrollVisible, setScrollVisible] = useState(true);
  const classes = useStyles(scrollVisible)();
  const history = useHistory();
  const { state = {} } = useLocation();
  const { jobId } = useParams();
  const [chunks, setChunks] = useState(null);

  const {
    data: jobDetail,
    isLoading: isJobDetailLoading,
    isSuccess: isJobDetailSuccess,
  } = useGetJobDetail(jobId);
  const [contentProvider, setContentProvider] = useState(null);
  const [contentProviderEndpoint, setContentProviderEndpoint] = useState(null);

  const contentProviderResponse = useContentProvider(
    jobDetail?.contentProviderId,
    { enabled: !!jobDetail?.contentProviderId },
  );

  const [inputFileDownloadUrl, setInputFileDownloadUrl] = useState(null);
  const [coverFileDownloadUrl, setCoverFileDownloadUrl] = useState(null);
  const [metadataFileDownloadUrl, setMetaDataFileDownloadUrl] = useState(null);
  useEffect(() => {
    async function fetchData() {
      if (notBlank(jobDetail)) {
        if (notBlank(jobDetail.inputFile)) {
          setInputFileDownloadUrl(await getDownloadUrl(jobDetail.inputFile));
        }

        if (notBlank(jobDetail.coverFile)) {
          setCoverFileDownloadUrl(await getDownloadUrl(jobDetail.coverFile));
        }

        if (
          notBlank(jobDetail.discoveryBucket) &&
          notBlank(jobDetail.discoveryFilename)
        ) {
          const metadataLocation = `s3://${jobDetail.discoveryBucket}/${jobDetail.discoveryPrefix}/${jobDetail.discoveryFilename}`;
          console.log(`metadata location: ${metadataLocation}`);
          setMetaDataFileDownloadUrl(await getDownloadUrl(metadataLocation));
        }
      }
    }

    fetchData();
  }, [jobDetail]);

  const contentProviderEndpointResponse = useContentProviderEndpoint(
    jobDetail?.contentProviderId,
    jobDetail?.contentProviderEndpointId,
    {
      enabled:
        !!jobDetail?.contentProviderId &&
        !!jobDetail?.contentProviderEndpointId,
    },
  );
  useEffect(() => {
    if (
      contentProviderResponse.isSuccess &&
      notBlank(contentProviderResponse.data)
    ) {
      setContentProvider(contentProviderResponse.data);
    }

    if (
      contentProviderEndpointResponse.isSuccess &&
      notBlank(contentProviderEndpointResponse.data)
    ) {
      setContentProviderEndpoint(contentProviderEndpointResponse.data);
    }
  }, [
    contentProviderEndpointResponse.data,
    contentProviderEndpointResponse.isSuccess,
    contentProviderResponse.data,
    contentProviderResponse.isSuccess,
  ]);

  const chunksResponse = useGetJobChunks(jobId);
  useEffect(() => {
    if (chunksResponse.isSuccess && notBlank(chunksResponse.data)) {
      setChunks(chunksResponse.data);
    }
  }, [chunksResponse.isSuccess, chunksResponse.data]);

  const [page, setPage] = useState(0);
  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  return (
    <Container id="chunk-list" classes={{ root: classes.root }}>
      <Header
        title="Job Details"
        selectedActions={
          <Box>
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() =>
                notBlank(state) && notBlank(state.previousLocation)
                  ? history.push(state.previousLocation)
                  : history.push(`/content-ingestion-report`)
              }
            >
              {notBlank(state) && notBlank(state.label)
                ? state.label
                : "Back to Browse"}
            </Button>
          </Box>
        }
      />
      <Container classes={{ root: classes.panel }}>
        {isJobDetailLoading && (
          <Container style={{ height: 400 }}>
            <PanelLoadingIndicator />
          </Container>
        )}
        {isJobDetailSuccess && notBlank(jobDetail) && (
          <Box m={4}>
            <Grid container spacing={2}>
              <Grid item sm={12}>
                <LabeledReadOnly label="Job Id">
                  <ReadOnlyField>{jobDetail.jobId}</ReadOnlyField>
                </LabeledReadOnly>
              </Grid>

              <Grid item sm={6}>
                <LabeledReadOnly label="Job Event Timestamp">
                  <ReadOnlyField>{jobDetail.eventTimeStamp}</ReadOnlyField>
                </LabeledReadOnly>
              </Grid>

              <Grid item sm={6}>
                <LabeledReadOnly label="Job Status">
                  <ReadOnlyField>{jobDetail.jobStatus}</ReadOnlyField>
                </LabeledReadOnly>
              </Grid>

              {notBlank(jobDetail.mediaId) && (
                <Grid item sm={6}>
                  <LabeledReadOnly label="Media Id">
                    <ReadOnlyField>{jobDetail.mediaId}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>
              )}

              {notBlank(jobDetail.ingestionJobType) && (
                <Grid item sm={6}>
                  <LabeledReadOnly label="Ingestion Type">
                    <ReadOnlyField>{jobDetail.ingestionJobType}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>
              )}

              {notBlank(jobDetail.inputFile) && (
                <Grid item sm={12}>
                  <LabeledReadOnly label="Input File">
                    <Box display="flex" alignSelf="center">
                      <Box>
                        <ReadOnlyField>{jobDetail.inputFile}</ReadOnlyField>
                      </Box>
                      {notBlank(inputFileDownloadUrl) && (
                        <Box mt={-2.5}>
                          <Tooltip title="Download">
                            <IconButton
                              href={inputFileDownloadUrl}
                              target="_blank"
                              download={generateFilename(jobDetail.inputFile)}
                            >
                              <DownloadIcon color="primary" fontSize="large" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </LabeledReadOnly>
                </Grid>
              )}

              {notBlank(jobDetail.coverFile) && (
                <Grid item sm={12}>
                  <LabeledReadOnly label="Cover File">
                    <Box display="flex" alignSelf="center">
                      <Box>
                        <ReadOnlyField>{jobDetail.coverFile}</ReadOnlyField>
                      </Box>
                      {notBlank(coverFileDownloadUrl) && (
                        <Box mt={-2.5}>
                          <Tooltip title="Download">
                            <IconButton
                              href={coverFileDownloadUrl}
                              target="_blank"
                              download={generateFilename(jobDetail.coverFile)}
                            >
                              <DownloadIcon color="primary" fontSize="large" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </LabeledReadOnly>
                </Grid>
              )}

              {notBlank(jobDetail.contentProviderId) && (
                <Grid item sm={6}>
                  <LabeledReadOnly label="Content Provider">
                    <ReadOnlyField>
                      {contentProvider?.name || jobDetail.contentProviderId}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>
              )}

              {notBlank(jobDetail.contentProviderEndpointId) && (
                <Grid item sm={6}>
                  <LabeledReadOnly label="Content Provider Endpoint">
                    <ReadOnlyField>
                      {contentProviderEndpoint?.name ||
                        jobDetail.contentProviderEndpointId}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>
              )}

              {notBlank(jobDetail.discoveryBucket) && (
                <Grid item sm={6}>
                  <LabeledReadOnly label="Discovery Location">
                    <ReadOnlyField>
                      {jobDetail.discoveryBucket +
                        "/" +
                        jobDetail.discoveryPrefix}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>
              )}

              {notBlank(jobDetail.discoveryFilename) && (
                <Grid item sm={6}>
                  <LabeledReadOnly label="Filename">
                    <Box display="flex" alignSelf="center">
                      <Box>
                        <ReadOnlyField>
                          {jobDetail.discoveryFilename}
                        </ReadOnlyField>
                      </Box>
                      {notBlank(metadataFileDownloadUrl) && (
                        <Box mt={-2.5}>
                          <Tooltip title="Download">
                            <IconButton
                              href={metadataFileDownloadUrl}
                              target="_blank"
                              download={generateFilename(
                                jobDetail.discoveryFilename,
                              )}
                            >
                              <DownloadIcon color="primary" fontSize="large" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </LabeledReadOnly>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {chunksResponse.isLoading && (
          <Container style={{ height: 400 }}>
            <PanelLoadingIndicator />
          </Container>
        )}
        {chunksResponse.isSuccess && notBlank(chunks) && (
          <JobChunksTable
            chunks={chunks}
            page={page}
            onPageChange={handlePageChange}
            onScrollVisible={handleScrollVisible}
          />
        )}
      </Container>
    </Container>
  );
};

export default ContentIngestionJobDetailView;
